import type React from 'react';
import { useRef, useState } from 'react';
import { CircleMinus, MoreHorizontal, FolderPlus, Edit, Mail, MailX, Trash2, Move, Waypoints } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useDeepAnalysisCostConfirmation } from '@/components/deep-analysis/DeepAnalysisCostConfirmation';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/shared/Spinner';
import { deleteFolder, setFolderCorrespondence, type FolderTree } from '@/hooks/useFolders';
import { DEEP_ANALYSIS_LABEL, setFolderAnalysisSetState } from '@/hooks/useAnalysisSet';
import { apiErrorMessage, apiErrorStatus } from '@/lib/api-client';
import {
  canToggleFolderCorrespondence,
  isCorrespondenceFolder,
  isInheritedCorrespondenceFolder,
} from '@/utils/document-utils';
import { isClientDocumentsRoot } from '@/lib/eca-client-documents';
import { CreateFolderDialog } from './CreateFolderDialog';
import { RenameFolderDialog } from './RenameFolderDialog';
import { DeleteFolderDialog } from './DeleteFolderDialog';
import { MoveFolderDialog } from './MoveFolderDialog';
import * as Tooltip from '@radix-ui/react-tooltip';

/** Matches the folder-row "Empty folder" tooltip: no subfolders and no documents. */
export function isEmptyFolder(folder: Pick<FolderTree, 'children' | 'document_count'>): boolean {
  return (folder.children?.length ?? 0) === 0 && (folder.document_count ?? 0) === 0;
}

const FOLDER_NOT_EMPTY_ERRORS = new Set([
  'Cannot delete folder with documents',
  'Cannot delete folder with subfolders',
]);

interface FolderActionsDropdownProps {
  matterSlug: string;
  folder: FolderTree;
  onRefresh: () => void;
  onDropdownOpenChange?: (open: boolean) => void;
  deepAnalysisEnabled?: boolean;
}

export const FolderActionsDropdown: React.FC<FolderActionsDropdownProps> = ({
  matterSlug,
  folder,
  onRefresh,
  onDropdownOpenChange,
  deepAnalysisEnabled = false,
}) => {
  const {
    confirmDeepAnalysisCost,
    deepAnalysisCostConfirmationDialog,
  } = useDeepAnalysisCostConfirmation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const handleDropdownOpenChange = (open: boolean) => {
    setDropdownOpen(open);
    onDropdownOpenChange?.(open);
  };
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [isUpdatingAnalysisSet, setIsUpdatingAnalysisSet] = useState(false);
  const [isUpdatingCorrespondence, setIsUpdatingCorrespondence] = useState(false);
  const deleteInFlightRef = useRef(false);

  const handleCreateSubfolder = () => {
    setDropdownOpen(false); // Close dropdown first
    setCreateDialogOpen(true);
  };

  const handleRenameFolder = () => {
    setDropdownOpen(false); // Close dropdown first
    setRenameDialogOpen(true);
  };

  const handleDeleteFolder = async () => {
    setDropdownOpen(false); // Close dropdown first
    // An empty folder has nothing to lose, so skip the irreversible-delete warning.
    // Delete without recursion: the tree count can omit in-progress files and, in
    // Deep Analysis, files outside the analysis set. The server refuses if the
    // folder is not actually empty, and we fall back to the confirmation.
    if (!isEmptyFolder(folder)) {
      setDeleteDialogOpen(true);
      return;
    }
    if (deleteInFlightRef.current) return;

    deleteInFlightRef.current = true;
    try {
      await deleteFolder(matterSlug, folder.guid, false, false, false);
      toast.success('Folder deleted');
      onRefresh();
    } catch (error) {
      if (apiErrorStatus(error) === 400 && FOLDER_NOT_EMPTY_ERRORS.has(apiErrorMessage(error) ?? '')) {
        setDeleteDialogOpen(true);
        return;
      }
      console.error('Error deleting folder:', error);
      toast.error(apiErrorMessage(error) || 'Failed to delete folder');
    } finally {
      deleteInFlightRef.current = false;
    }
  };

  const handleMoveFolder = () => {
    setDropdownOpen(false); // Close dropdown first
    setMoveDialogOpen(true);
  };

  const handleToggleCorrespondence = async () => {
    const nextState = !folder.is_correspondence;
    setIsUpdatingCorrespondence(true);
    try {
      await setFolderCorrespondence(matterSlug, folder.guid, nextState);
      toast.success(
        nextState
          ? 'Folder marked as a Correspondence folder'
          : 'Folder unmarked as a Correspondence folder',
      );
      setDropdownOpen(false);
      onRefresh();
    } catch (error) {
      console.error('Correspondence folder update error:', error);
      toast.error(apiErrorMessage(error) || 'Failed to update Correspondence');
    } finally {
      setIsUpdatingCorrespondence(false);
    }
  };

  const handleToggleAnalysisSet = async () => {
    const nextState = folder.analysis_set_effective ? 'excluded' : 'included';
    if (nextState === 'included') {
      const fileCount = folder.analysis_set_addable_document_count ?? folder.document_count ?? 0;
      const tokenCount = folder.analysis_set_addable_token_count ?? folder.token_count;
      const confirmed = await confirmDeepAnalysisCost({
        documentCount: fileCount,
        tokenCount,
        estimateNote: fileCount === 0
          ? `Future files added here may incur ${DEEP_ANALYSIS_LABEL} costs.`
          : undefined,
        scopeLabel: fileCount > 0
          ? `up to ${fileCount} file${fileCount !== 1 ? 's' : ''}`
          : 'No current files to estimate',
      });
      if (!confirmed) return;
    }

    setIsUpdatingAnalysisSet(true);
    try {
      await setFolderAnalysisSetState(matterSlug, folder.guid, nextState);
      toast.success(
        folder.analysis_set_effective
          ? `Removed folder from ${DEEP_ANALYSIS_LABEL}`
          : `Marked folder for ${DEEP_ANALYSIS_LABEL}`,
      );
      setDropdownOpen(false);
      onRefresh();
    } catch (error) {
      console.error('Analysis Set folder update error:', error);
      toast.error(`Failed to update ${DEEP_ANALYSIS_LABEL}`);
    } finally {
      setIsUpdatingAnalysisSet(false);
    }
  };

  const isSyncedFolder = folder.is_synced === true;
  const hasSyncProviders = (folder.sync_providers?.length ?? 0) > 0;
  const isDeleteRestrictedBySync = isSyncedFolder || hasSyncProviders;
  const isRootCorrespondence = isCorrespondenceFolder(folder);
  const isRootClientDocuments = isClientDocumentsRoot(folder);
  const isInheritedCorrespondence = isInheritedCorrespondenceFolder(folder);
  const canModifyFolder = !isRootCorrespondence && !isRootClientDocuments && !isSyncedFolder;
  const syncProviderLabel = folder.sync_providers?.length
    ? folder.sync_providers.map(p => p === 'imanage' ? 'iManage' : p === 'outlook' ? 'Outlook' : p).join(' and ')
    : 'an external source';

  return (
    <>
      {deepAnalysisCostConfirmationDialog}
      <DropdownMenu open={dropdownOpen} onOpenChange={handleDropdownOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <Tooltip.Provider>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleCreateSubfolder}>
              <FolderPlus className="mr-2 h-4 w-4" />
              Create Subfolder
            </DropdownMenuItem>
            {canToggleFolderCorrespondence(folder) && (
              <DropdownMenuItem
                onClick={handleToggleCorrespondence}
                disabled={isUpdatingCorrespondence}
              >
                {isUpdatingCorrespondence ? (
                  <Spinner size="xs" className="mr-2" />
                ) : folder.is_correspondence ? (
                  <MailX className="mr-2 h-4 w-4" />
                ) : (
                  <Mail className="mr-2 h-4 w-4" />
                )}
                {folder.is_correspondence
                  ? 'Unmark as Correspondence folder'
                  : 'Mark as Correspondence folder'}
              </DropdownMenuItem>
            )}
            {isInheritedCorrespondence && (
              <Tooltip.Root delayDuration={300}>
                <Tooltip.Trigger asChild>
                  <div>
                    <DropdownMenuItem disabled className="cursor-not-allowed">
                      <Mail className="mr-2 h-4 w-4" />
                      Included in Correspondence
                    </DropdownMenuItem>
                  </div>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    className="z-50 max-w-xs rounded-sm bg-gray-900 px-2 py-1 text-xs text-white shadow-lg"
                    sideOffset={5}
                  >
                    Included through a parent Correspondence folder
                    <Tooltip.Arrow className="fill-gray-900" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            )}
            {deepAnalysisEnabled && (
              <DropdownMenuItem
                onClick={handleToggleAnalysisSet}
                disabled={isUpdatingAnalysisSet}
              >
                {folder.analysis_set_effective ? (
                  <CircleMinus className="mr-2 h-4 w-4" />
                ) : (
                  <Waypoints className="mr-2 h-4 w-4" />
                )}
                {isUpdatingAnalysisSet
                  ? 'Updating...'
                  : folder.analysis_set_effective
                    ? 'Remove from Deep Analysis'
                    : 'Mark for Deep Analysis'}
              </DropdownMenuItem>
            )}
            {canModifyFolder ? (
              <>
                <DropdownMenuItem onClick={handleRenameFolder}>
                  <Edit className="mr-2 h-4 w-4" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleMoveFolder}>
                  <Move className="mr-2 h-4 w-4" />
                  Move
                </DropdownMenuItem>
                {isDeleteRestrictedBySync ? (
                  <Tooltip.Root delayDuration={300}>
                    <Tooltip.Trigger asChild>
                      <div>
                        <DropdownMenuItem disabled className="cursor-not-allowed">
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </div>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content
                        className="z-50 px-2 py-1 text-xs bg-gray-900 text-white rounded-sm shadow-lg max-w-xs"
                        sideOffset={5}
                      >
                        This folder is synced from {syncProviderLabel} and cannot be deleted
                        <Tooltip.Arrow className="fill-gray-900" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                ) : (
                  <DropdownMenuItem onClick={handleDeleteFolder} className="text-red-600">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                )}
              </>
            ) : isSyncedFolder ? (
              <>
                <Tooltip.Root delayDuration={300}>
                  <Tooltip.Trigger asChild>
                    <div>
                      <DropdownMenuItem disabled className="cursor-not-allowed">
                        <Edit className="mr-2 h-4 w-4" />
                        Rename
                      </DropdownMenuItem>
                    </div>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="z-50 px-2 py-1 text-xs bg-gray-900 text-white rounded-sm shadow-lg max-w-xs"
                      sideOffset={5}
                    >
                      This folder is synced from {syncProviderLabel} and cannot be renamed
                      <Tooltip.Arrow className="fill-gray-900" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
                <Tooltip.Root delayDuration={300}>
                  <Tooltip.Trigger asChild>
                    <div>
                      <DropdownMenuItem disabled className="cursor-not-allowed">
                        <Move className="mr-2 h-4 w-4" />
                        Move
                      </DropdownMenuItem>
                    </div>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="z-50 px-2 py-1 text-xs bg-gray-900 text-white rounded-sm shadow-lg max-w-xs"
                      sideOffset={5}
                    >
                      This folder is synced from {syncProviderLabel} and cannot be moved
                      <Tooltip.Arrow className="fill-gray-900" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
                <Tooltip.Root delayDuration={300}>
                  <Tooltip.Trigger asChild>
                    <div>
                      <DropdownMenuItem disabled className="cursor-not-allowed">
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </div>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="z-50 px-2 py-1 text-xs bg-gray-900 text-white rounded-sm shadow-lg max-w-xs"
                      sideOffset={5}
                    >
                      This folder is synced from {syncProviderLabel} and cannot be deleted
                      <Tooltip.Arrow className="fill-gray-900" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </>
            ) : isRootCorrespondence ? (
              null
            ) : isRootClientDocuments ? (
              <>
                <Tooltip.Root delayDuration={300}>
                  <Tooltip.Trigger asChild>
                    <div>
                      <DropdownMenuItem disabled className="cursor-not-allowed">
                        <Edit className="mr-2 h-4 w-4" />
                        Rename
                      </DropdownMenuItem>
                    </div>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="z-50 px-2 py-1 text-xs bg-gray-900 text-white rounded-sm shadow-lg max-w-xs"
                      sideOffset={5}
                    >
                      The Client Documents folder is a protected system folder and cannot be renamed
                      <Tooltip.Arrow className="fill-gray-900" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
                <Tooltip.Root delayDuration={300}>
                  <Tooltip.Trigger asChild>
                    <div>
                      <DropdownMenuItem disabled className="cursor-not-allowed">
                        <Move className="mr-2 h-4 w-4" />
                        Move
                      </DropdownMenuItem>
                    </div>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="z-50 px-2 py-1 text-xs bg-gray-900 text-white rounded-sm shadow-lg max-w-xs"
                      sideOffset={5}
                    >
                      The Client Documents folder is a protected system folder and cannot be moved
                      <Tooltip.Arrow className="fill-gray-900" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
                <Tooltip.Root delayDuration={300}>
                  <Tooltip.Trigger asChild>
                    <div>
                      <DropdownMenuItem disabled className="cursor-not-allowed">
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </div>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="z-50 px-2 py-1 text-xs bg-gray-900 text-white rounded-sm shadow-lg max-w-xs"
                      sideOffset={5}
                    >
                      The Client Documents folder is a protected system folder and cannot be deleted
                      <Tooltip.Arrow className="fill-gray-900" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </>
            ) : (
              null
            )}
          </DropdownMenuContent>
        </Tooltip.Provider>
      </DropdownMenu>

      <CreateFolderDialog
        matterSlug={matterSlug}
        parentFolder={folder}
        isOpen={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={onRefresh}
      />

      <RenameFolderDialog
        matterSlug={matterSlug}
        folder={folder}
        isOpen={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        onSuccess={onRefresh}
      />

      <DeleteFolderDialog
        matterSlug={matterSlug}
        folder={folder}
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onSuccess={onRefresh}
      />

      <MoveFolderDialog
        isOpen={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
        folder={folder}
        onSuccess={onRefresh}
      />
    </>
  );
};
